import { useNavigate } from "react-router-dom";
import { Button } from "../Catalyst/button";
import { Table, TableHead, TableRow, TableHeader, TableBody, TableCell } from "../Catalyst/table";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../store/store";
import { useEffect, useState } from "react";
import { fetchSearchedUsersListAsync } from "../../store/thunks/UsersThunk";
import useOrgId from "../../hooks/useOrgId";

interface User {
  id: string;
  organization_id: number;
  email: string;
  email_verified: boolean;
  first: string;
  middle: string | null;
  last: string;
  mobile_phone: string | null;
  job_title: string | null;
  user_role: string;
  created_at: string;
  updated_at: string;
  onboarding_state: string | null;
  terms_accepted_at: string | null;
  deactivated_at: string | null;
  invitation_sent_at: string | null;
  external_unique_id: string;
  is_active: boolean;

}

interface UsersTableProps {
  searchText?: string | undefined;
}

const UserListTable = ({ searchText }: UsersTableProps) => {
  const selectedOrgId = useOrgId();

  const { total_pages, filteredUsers } = useSelector(
    (state: RootState) => state.UserStore
  );
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [perPage] = useState<number>(10);
  const dispatch = useDispatch<AppDispatch>();

  //rerender if selectedOrgId changes
  useEffect(() => {
    setCurrentPage(1);
  }, [selectedOrgId]);

  useEffect(() => {
    if (!selectedOrgId) return;

    dispatch(
      fetchSearchedUsersListAsync({
        page: currentPage,
        per_page: perPage,
        orgId: selectedOrgId,
        searchString: searchText || "",
      })
    );
  }, [currentPage, perPage, selectedOrgId, searchText, dispatch]);

  const formatDate = (dateString: string | null) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getStatusText = (user: User) => {
    const baseStyles = "font-inter text-sm font-medium leading-5";

    if (user?.is_active === false) {
      return (
        <span className={`${baseStyles} text-red-400`}>
          Deactivated on {formatDate(user.deactivated_at)}
        </span>
      );
    } else if (!user?.onboarding_state) {
    return (
        <span className={`${baseStyles} text-text-primary-900 opacity-50`}>
          {user.invitation_sent_at ? `Invite sent on ${formatDate(user.invitation_sent_at)}` : 'Invite sent'}
        </span>
      );
    } else if(user?.onboarding_state === "ONBOARDING_COMPLETED") {
      return (
        <span className={`${baseStyles} text-green-600`}>
          {user?.terms_accepted_at ? `Active since ${formatDate(user.terms_accepted_at)}` : 'Active'}
        </span>
      );
    }else{
      return (
        <span className={`${baseStyles} text-yellow-500`}>
          Signed Up
        </span>
      );
    }
  };

  const getUserNameStyle = (user: User) => {
    if (user.deactivated_at || !user.onboarding_state) {
      return "font-medium opacity-50";
    }
    return "font-medium";
  };


  const navigate = useNavigate();

  return (
    <div className="px-6">
      {
        selectedOrgId ? 
             <Table>
        <TableHead>
          <TableRow className="bg-[#F9FAFB] border border-x-[#EAECF0]">
            <TableHeader>Name</TableHeader>
            <TableHeader className="w-[150px]" />
            <TableHeader>Status</TableHeader>
            <TableHeader>Actions</TableHeader>
          </TableRow>
        </TableHead>
        <TableBody>
          {filteredUsers.length > 0 && filteredUsers.map((user: User) => (
            <TableRow key={user.id}>
              <TableCell className={getUserNameStyle(user)}>
                {user.first} {user.last}
              </TableCell>
              <TableCell />
              <TableCell>
                {getStatusText(user)}
              </TableCell>
              <TableCell>
                <Button 
                  outline 
                  onClick={() => navigate(`/user-profile/${selectedOrgId}/${user.id}`)}
                  className="bg-textColor2"
                >
                  <span className="desktext13">View Activity</span>
                </Button>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      : 
          <div className="h-[692px] border-b-[1px] border-[#EAECF0] inset-0 flex items-center justify-center pointer-events-none">
            <p className="text-sm text-[#667085] text-center max-w-md">
              No Organization Selected. Select an Organization from the
              Organizations tab to populate the user list.
            </p>
          </div>
      }
 

      <div className="flex items-center justify-between px-2 py-3 text-sm text-zinc-600">
        <div>
          Page {currentPage} of {total_pages || 1}
        </div>
        <div className="flex gap-2">
          <Button
            outline
            disabled={currentPage === 1}
            onClick={() => setCurrentPage(currentPage - 1)}
          >
            Previous
          </Button>

          {[...Array(total_pages)].map((_, index) => {
            const pageNum = index + 1;
            const shouldShow =
              total_pages <= 5 ||
              pageNum === 1 ||
              pageNum === total_pages ||
              Math.abs(currentPage - pageNum) <= 1;

            if (!shouldShow) return null;

            return (
              <Button
                key={pageNum}
                {...(currentPage === pageNum ? { color: "blue" } : { outline: true })}
                onClick={() => setCurrentPage(pageNum)}
              >
                {pageNum}
              </Button>
            );
          })}

          {total_pages > 5 && currentPage < total_pages - 2 && (
            <span className="px-2">...</span>
          )}

          <Button
            outline
            disabled={currentPage === total_pages || total_pages === 0}
            onClick={() => setCurrentPage(currentPage + 1)}
          >
            Next
          </Button>
        </div>
      </div>
    </div>
  );
};

export default UserListTable;
